import { useState } from "react";
import axios from "axios";
import BusyButton from "../components/basic/busy-button";
import Checkbox from "../components/basic/checkbox";
import ConfirmDialog, {
  type ConfirmDialogProps,
} from "../components/basic/confirm-dialog";
import { apiHttpUrl } from "../helpers/api-base";
import { getAuthToken } from "../helpers/auth";
import type { AccountRow } from "../params/types";

type Group = "avatar" | "photos" | "videos" | "reels";

interface RemoteItem {
  code: string;
  group: Exclude<Group, "avatar">;
  kind: "image" | "video";
  thumb: string;
  count: number;
}

interface Listing {
  username: string;
  account: string;
  avatar: string;
  items: RemoteItem[];
}

const LIMIT_OPTIONS = [10, 30, 50, 100, 200, 500, 750, 1000];

const GROUP_DEFS: { id: Group; label: string }[] = [
  { id: "avatar", label: "Avatar" },
  { id: "photos", label: "Images" },
  { id: "videos", label: "Videos" },
  { id: "reels", label: "Reels" },
];

const AVATAR_KEY = "avatar";

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface MigrationRemoverProps {
  accounts?: AccountRow[];
  setError?: (message: string) => void;
}

export default function MigrationRemover({
  accounts = [],
  setError,
}: MigrationRemoverProps) {
  const [account, setAccount] = useState("");
  const [maxPosts, setMaxPosts] = useState(10);
  const [listing, setListing] = useState<Listing | null>(null);
  /** Selected keys: a post's code, or AVATAR_KEY. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState<Omit<
    ConfirmDialogProps,
    "onCancel"
  > | null>(null);

  const busy = loading || deleting;

  const selectableKeys = listing
    ? [
        ...(listing.avatar ? [AVATAR_KEY] : []),
        ...listing.items.map((i) => i.code),
      ]
    : [];
  const selectedCount = selectableKeys.filter((k) => selected.has(k)).length;
  const allSelected =
    selectableKeys.length > 0 && selectedCount === selectableKeys.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const load = async () => {
    if (!account || busy) return;
    setLoading(true);
    setMessage("");
    setSelected(new Set());
    try {
      const res = await axios.post(
        apiHttpUrl("/api/migration/remote/list"),
        { account, max_posts: maxPosts },
        { headers: authHeaders(), validateStatus: () => true, timeout: 0 },
      );
      const data = res.data || {};
      if (!data.ok) {
        setError?.(data.error || "Unable to load the account's media.");
        return;
      }
      setListing({
        username: data.username || account,
        account,
        avatar: data.avatar || "",
        items: data.items || [],
      });
    } catch {
      setError?.("Unable to reach the server.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key: string, on: boolean) =>
    setSelected((p) => {
      const next = new Set(p);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const doDelete = async () => {
    if (!listing) return;
    const codes = listing.items
      .filter((i) => selected.has(i.code))
      .map((i) => i.code);
    const avatar = selected.has(AVATAR_KEY);
    setDeleting(true);
    setMessage("");
    try {
      const res = await axios.post(
        apiHttpUrl("/api/migration/remote/delete"),
        { account: listing.account, codes, avatar },
        { headers: authHeaders(), validateStatus: () => true, timeout: 0 },
      );
      const data = res.data || {};
      if (data.error) setError?.(data.error);
      const deleted: string[] = data.deleted || [];
      const failed: string[] = data.failed || [];
      setListing((l) =>
        l
          ? {
              ...l,
              avatar: deleted.includes("avatar") ? "" : l.avatar,
              items: l.items.filter((i) => !deleted.includes(i.code)),
            }
          : l,
      );
      setSelected((p) => {
        const next = new Set(p);
        deleted.forEach((k) => next.delete(k === "avatar" ? AVATAR_KEY : k));
        return next;
      });
      setMessage(
        `Deleted ${deleted.length} item(s).` +
          (failed.length ? ` ${failed.length} failed: ${failed[0]}` : ""),
      );
    } catch {
      setError?.("Unable to reach the server.");
    } finally {
      setDeleting(false);
    }
  };

  const onDelete = () => {
    if (!listing || selectedCount === 0 || busy) return;
    setConfirm({
      title: "Delete from Instagram?",
      rows: [
        { label: "Items", value: selectedCount },
        { label: "Account", value: `@${listing.username}` },
      ],
      note: "This permanently deletes the selected items from the account on Instagram. It cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
      onConfirm: () => {
        setConfirm(null);
        void doDelete();
      },
    });
  };

  return (
    <div className="ib-migration-view">
      <div className="ib-settings-section ib-migration-card">
        <div className="ib-migration-bar">
          <label className="ib-migration-limit">
            <span>Account</span>
            <select
              className="ib-migration-select"
              value={account}
              onChange={(e) => {
                setAccount(e.target.value);
                setListing(null);
                setSelected(new Set());
                setMessage("");
              }}
              disabled={busy}
            >
              <option value="">Select…</option>
              {accounts
                .filter((a) => a.session_status !== "banned")
                .map((a) => (
                  <option key={a.username} value={a.username}>
                    {a.username}
                    {a.ig_username ? ` (@${a.ig_username})` : ""}
                  </option>
                ))}
            </select>
          </label>
          <label className="ib-migration-limit">
            <span>Latest posts</span>
            <select
              className="ib-migration-select"
              value={maxPosts}
              onChange={(e) => setMaxPosts(Number(e.target.value))}
              disabled={busy}
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <BusyButton
            variant="jf-btn jf-btn-primary ib-migration-btn"
            label="OK"
            busy={loading}
            busyLabel="Loading…"
            disabled={!account || deleting}
            onClick={load}
          />
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            disabled={busy || selectableKeys.length === 0}
            label={
              selectedCount > 0 ? `${selectedCount} selected` : "Select all"
            }
            onChange={(on) =>
              setSelected(on ? new Set(selectableKeys) : new Set())
            }
          />
          <BusyButton
            variant="jf-btn jf-btn-danger-outline ib-migration-btn"
            label="Delete"
            busy={deleting}
            busyLabel="Deleting…"
            disabled={selectedCount === 0 || loading}
            onClick={onDelete}
          />
        </div>
        {loading && (
          <p className="ib-migration-meta">
            Loading… this opens the account&apos;s browser and can take a
            minute.
          </p>
        )}
        {deleting && (
          <p className="ib-migration-meta">
            Deleting… this opens the account&apos;s browser and can take
            several minutes.
          </p>
        )}
        {message && <p className="ib-migration-meta">{message}</p>}
      </div>

      {listing &&
        GROUP_DEFS.map(({ id, label }) => {
          const items =
            id === "avatar" ? [] : listing.items.filter((i) => i.group === id);
          const count = id === "avatar" ? (listing.avatar ? 1 : 0) : items.length;
          return (
            <section key={id} className="ib-migration-group">
              <h3 className="ib-migration-group__title">
                {label} <span>({count})</span>
              </h3>
              {count === 0 ? (
                <p className="ib-migration-empty">Nothing found.</p>
              ) : (
                <div className="ib-migration-grid">
                  {id === "avatar" ? (
                    <div className="ib-migration-card-item">
                      <Checkbox
                        className="ib-checkbox--tile"
                        checked={selected.has(AVATAR_KEY)}
                        disabled={busy}
                        onChange={(on) => toggle(AVATAR_KEY, on)}
                      />
                      <div className="ib-migration-item">
                        <img
                          src={listing.avatar}
                          alt="avatar"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                  ) : (
                    items.map((i) => (
                      <div key={i.code} className="ib-migration-card-item">
                        {i.count > 1 && (
                          <span
                            className="ib-group-badge"
                            title={`One post with ${i.count} photos/videos; deleting it removes all of them`}
                          >
                            {i.count} items
                          </span>
                        )}
                        <Checkbox
                          className="ib-checkbox--tile"
                          checked={selected.has(i.code)}
                          disabled={busy}
                          title={i.code}
                          onChange={(on) => toggle(i.code, on)}
                        />
                        <a
                          className="ib-migration-item"
                          href={`https://www.instagram.com/${
                            i.group === "reels" ? "reel" : "p"
                          }/${i.code}/`}
                          target="_blank"
                          rel="noreferrer"
                          title={i.code}
                        >
                          <img
                            src={i.thumb}
                            alt={i.code}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        </a>
                      </div>
                    ))
                  )}
                </div>
              )}
            </section>
          );
        })}
      {confirm && (
        <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}
